import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { FirmDailyNavConfigVersion } from 'api';
import {
  buildDailyNavFirmConfigDiff,
  DAILY_NAV_THRESHOLD_KEYS,
  DailyNavConfigDiffRow,
  dailyNavNextDayLabel,
} from 'common/constants/dailyNav';
import {
  Box,
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Stack,
  Text,
  TextLink,
} from 'components/ui';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';
import { DAILY_NAV_SETUP_STEPS, DailyNavSetupStep } from '../dailyNavSetup.utils';

const ftpDisplay = (config: FirmDailyNavConfigVersion) => {
  if (!config.ftp_host) {
    return 'None';
  }
  const user = config.ftp_username ? `${config.ftp_username}@` : '';
  return `${user}${config.ftp_host}:${config.ftp_port ?? ''}${config.ftp_path ?? ''}`;
};

// The shared diff covers what Daily NAV Settings shows; the wizard also edits these.
const extraRows = (saved: FirmDailyNavConfigVersion, draft: FirmDailyNavConfigVersion): DailyNavConfigDiffRow[] => {
  const rows: DailyNavConfigDiffRow[] = [];
  if ((saved.report_template ?? null) !== (draft.report_template ?? null)) {
    rows.push({
      key: 'report_template',
      label: 'Report template',
      from: saved.report_template_filename || 'None',
      to: draft.report_template_filename || 'None',
    });
  } else if (JSON.stringify(saved.report_column_map ?? []) !== JSON.stringify(draft.report_column_map ?? [])) {
    rows.push({ key: 'report_column_map', label: 'Template column mapping', from: 'Previous mapping', to: 'Updated' });
  }
  if (ftpDisplay(saved) !== ftpDisplay(draft)) {
    rows.push({ key: 'ftp', label: 'SFTP delivery', from: ftpDisplay(saved), to: ftpDisplay(draft) });
  }
  return rows;
};

// The wizard's own names where they differ from the shared diff's.
const WIZARD_LABELS: Record<string, string> = {
  report_send_time: 'Reminder time',
  notification_recipients: 'Reminder recipients',
};

const MARKET_KEYS = ['trading_calendar', 'monitoring_mode', 'report_send_time', 'notification_recipients'];

const stepFor = (key: string): DailyNavSetupStep => {
  if (MARKET_KEYS.includes(key)) {
    return DailyNavSetupStep.MARKET;
  }
  if ((DAILY_NAV_THRESHOLD_KEYS as readonly string[]).includes(key) || key.endsWith('_window_sessions')) {
    return DailyNavSetupStep.THRESHOLDS;
  }
  return DailyNavSetupStep.DELIVERY;
};

// What a firm save changes, grouped by the wizard step that edits it.
export const buildFirmSaveReviewGroups = (saved: FirmDailyNavConfigVersion, draft: FirmDailyNavConfigVersion) => {
  const rows = [...buildDailyNavFirmConfigDiff(saved, draft), ...extraRows(saved, draft)].map(row => ({
    ...row,
    label: WIZARD_LABELS[row.key] ?? row.label,
  }));
  return DAILY_NAV_SETUP_STEPS.map(step => ({ step, rows: rows.filter(row => stepFor(row.key) === step.key) })).filter(
    group => group.rows.length
  );
};

type FirmSaveReviewDialogProps = {
  open: boolean;
  groups: ReturnType<typeof buildFirmSaveReviewGroups>;
  nextNavDate?: string | null;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  // Closes the dialog on the step that holds the change, keeping the draft.
  onEditStep: (step: DailyNavSetupStep) => void;
};

// Edit mode only: what the save changes, before it opens a new version for the firm and every company.
const FirmSaveReviewDialog = ({
  open,
  groups,
  nextNavDate,
  isSaving,
  onCancel,
  onConfirm,
  onEditStep,
}: FirmSaveReviewDialogProps) => (
  <Dialog
    open={open}
    onClose={isSaving ? undefined : onCancel}
    fullWidth
    // Wide enough for the Setting / Saved / New table without wrapping labels.
    PaperProps={{ sx: { maxWidth: 680 } }}
    data-testid={DAILY_NAV_SETUP_TEST_IDS.firmSaveDialog}>
    <DialogHeader>
      <DialogTitle>Review firm setting changes</DialogTitle>
      <DialogCloseButton aria-label="Close" size="small" onClick={onCancel} disabled={isSaving}>
        <DialogCloseIcon />
      </DialogCloseButton>
    </DialogHeader>
    <DialogContent>
      <DialogDescription>
        Saving opens a new version for the firm and every company. {dailyNavNextDayLabel(nextNavDate)}.
      </DialogDescription>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {groups.length ? (
          groups.map(({ step, rows }) => (
            <Box key={step.key} data-testid={DAILY_NAV_SETUP_TEST_IDS.firmSaveGroup(step.key)}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Text variant="subtitle2">{step.label}</Text>
                <TextLink
                  component="button"
                  type="button"
                  onClick={() => onEditStep(step.key)}
                  disabled={isSaving}
                  data-testid={DAILY_NAV_SETUP_TEST_IDS.firmSaveEditStep(step.key)}>
                  Edit
                </TextLink>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '40%' }}>Setting</TableCell>
                    <TableCell>Saved</TableCell>
                    <TableCell>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.key}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>{row.from}</TableCell>
                      <TableCell sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{row.to}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))
        ) : (
          <Text variant="body2" color="text.secondary">
            No field-level changes.
          </Text>
        )}
        <Text variant="body2" color="text.secondary">
          If a NAV day is in progress, you can apply these settings to it at Review.
        </Text>
      </Stack>
    </DialogContent>
    <DialogActionFooter
      actionLabel="Save changes"
      cancelLabel="Keep editing"
      onAction={onConfirm}
      onCancel={onCancel}
      isActionLoading={isSaving}
      isActionDisabled={isSaving}
      actionButtonTestId={DAILY_NAV_SETUP_TEST_IDS.firmSaveConfirmButton}
    />
  </Dialog>
);

export default FirmSaveReviewDialog;
